import React, { useState, useMemo } from 'react';
import { useProgress } from '../context/ProgressContext';
import { TrophyCard } from '../components';
import { trophies } from '../data/trophies';
import type { Trophy } from '../data/trophies';
import './TrophyRoomPage.css';

type Category = 'all' | Trophy['categorie'];

export const TrophyRoomPage: React.FC = () => {
  const { progress, aiTrophies } = useProgress();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<Category>('all');
  const [showUnlockedOnly, setShowUnlockedOnly] = useState(false);

  const categories: { key: Category; label: string; count: number }[] = useMemo(() => {
    const counts = {
      all: trophies.length + aiTrophies.length,
      web: trophies.filter((t) => t.categorie === 'web').length,
      game: trophies.filter((t) => t.categorie === 'game').length,
      ai: trophies.filter((t) => t.categorie === 'ai').length + aiTrophies.length,
      bonus: trophies.filter((t) => t.categorie === 'bonus').length,
    };

    return [
      { key: 'all', label: 'Toate', count: counts.all },
      { key: 'web', label: 'Web', count: counts.web },
      { key: 'game', label: 'Joc', count: counts.game },
      { key: 'ai', label: 'AI', count: counts.ai },
      { key: 'bonus', label: 'Bonus', count: counts.bonus },
    ];
  }, [aiTrophies.length]);

  const filteredTrophies = useMemo(() => {
    let result = [...trophies];

    if (selectedCategory !== 'all') {
      result = result.filter((t) => t.categorie === selectedCategory);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (t) =>
          t.nume.toLowerCase().includes(term) ||
          t.descriere.toLowerCase().includes(term)
      );
    }

    if (showUnlockedOnly) {
      result = result.filter((t) => progress.trophiesUnlocked[t.id]);
    }

    return result;
  }, [selectedCategory, searchTerm, showUnlockedOnly, progress.trophiesUnlocked]);

  const filteredAITrophies = useMemo(() => {
    if (selectedCategory !== 'all' && selectedCategory !== 'ai') {
      return [];
    }

    if (!searchTerm) return aiTrophies;

    const term = searchTerm.toLowerCase();
    return aiTrophies.filter(
      (t) =>
        t.name.toLowerCase().includes(term) ||
        t.description.toLowerCase().includes(term)
    );
  }, [selectedCategory, searchTerm, aiTrophies]);

  const unlockedCount = Object.keys(progress.trophiesUnlocked).length + aiTrophies.length;
  const totalCount = trophies.length;

  const resetFilters = () => {
    setSearchTerm('');
    setSelectedCategory('all');
    setShowUnlockedOnly(false);
  };

  const hasActiveFilters = searchTerm || selectedCategory !== 'all' || showUnlockedOnly;

  return (
    <div className="trophy-room-page">
      <header className="trophy-room-header">
        <h1>Camera Trofeelor</h1>
        <p className="trophy-room-subtitle">
          {unlockedCount} din {totalCount} trofee deblocate
          {aiTrophies.length > 0 && ` + ${aiTrophies.length} create cu AI`}
        </p>
      </header>

      <div className="trophy-room-controls">
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Caută trofee..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          {searchTerm && (
            <button
              className="search-clear"
              onClick={() => setSearchTerm('')}
              aria-label="Șterge căutarea"
              type="button"
            >
              ×
            </button>
          )}
        </div>

        <div className="filter-tabs">
          {categories.map((cat) => (
            <button
              key={cat.key}
              className={`filter-tab ${selectedCategory === cat.key ? 'active' : ''}`}
              onClick={() => setSelectedCategory(cat.key)}
              type="button"
            >
              {cat.label}
              <span className="filter-count">{cat.count}</span>
            </button>
          ))}
        </div>

        <label className="unlocked-toggle">
          <input
            type="checkbox"
            checked={showUnlockedOnly}
            onChange={(e) => setShowUnlockedOnly(e.target.checked)}
          />
          <span>Doar deblocate</span>
        </label>

        {hasActiveFilters && (
          <button className="reset-filters-btn" onClick={resetFilters} type="button">
            Resetează filtre
          </button>
        )}
      </div>

      {filteredTrophies.length === 0 && filteredAITrophies.length === 0 ? (
        <div className="no-results">
          <span className="no-results-icon">🔍</span>
          <p>Nu s-au găsit trofee pentru filtrele selectate.</p>
          <button className="btn btn-secondary" onClick={resetFilters} type="button">
            Resetează filtre
          </button>
        </div>
      ) : (
        <>
          {filteredAITrophies.length > 0 && (
            <section className="ai-trophies-section">
              <h2>Trofee Generate cu AI</h2>
              <div className="trophies-grid">
                {filteredAITrophies.map((trophy) => (
                  <TrophyCard
                    key={trophy.id}
                    trophy={{
                      id: trophy.id,
                      nume: trophy.name,
                      descriere: trophy.description,
                      categorie: 'ai',
                      icon: '',
                      aiPrompt: trophy.prompt,
                    }}
                    isUnlocked={true}
                    unlockedAt={trophy.createdAt}
                    isAI={true}
                    imageUrl={trophy.imageDataUrl}
                  />
                ))}
              </div>
            </section>
          )}

          <div className="trophies-grid">
            {filteredTrophies.map((trophy) => {
              const unlockData = progress.trophiesUnlocked[trophy.id];
              return (
                <TrophyCard
                  key={trophy.id}
                  trophy={trophy}
                  isUnlocked={!!unlockData}
                  unlockedAt={unlockData?.unlockedAt}
                />
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};
